import { Slider } from '@/components/ui/slider';
import { useEffect } from 'react';
import type { FDI_Notation } from '@common/FDI_Notation';
import { MID_TEETH_FDI, WIDE_TEETH_FDI } from '@bracesDesigner/constants/FDI_NotationConst';
import { MID_START_BASELINE } from '@segmentation/constants/SelectionLimitsConst';
import useTeethSelectionStore from '../hooks/useTeethSelectionStore';

interface SelectionLimitsProps {
  selectedFDI?: FDI_Notation;
}

export default function SelectionLimits({ selectedFDI }: SelectionLimitsProps) {
  const {
    startStop,
    initialStartStop,
    endStop,
    initialEndStop,
    setStartStop,
    setEndStop,
  } = useTeethSelectionStore();

  // adjust stores whenever the selected tooth changes
  useEffect(() => {
    if (selectedFDI === undefined) return;

    if (WIDE_TEETH_FDI.includes(selectedFDI)) {
      setStartStop(initialStartStop);
      setEndStop(initialEndStop);
      return;
    }

    if (MID_TEETH_FDI.includes(selectedFDI)) {
      setStartStop(MID_START_BASELINE);
      setEndStop(initialEndStop * 0.9);
      return;
    }

    console.warn('SelectionLimits: unexpected/unlisted selectedFDI', selectedFDI);
  }, [
    selectedFDI,
    initialStartStop,
    initialEndStop,
    setStartStop,
    setEndStop,
  ]);
  
  return (
    <section className="rounded-3xl border border-content-highlight/60 bg-content/80 p-6 text-white shadow-xl shadow-content-dark/20 backdrop-blur">
      <header className="space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Fine-tune</p>
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold tracking-tight">Selection limits </h3>
        </div>
        <p className="text-sm text-white/70">Adjust the start and end thresholds to refine the automated selection.</p>
      </header>

      <div className="mt-5 space-y-5">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-white/60">
            <span>Start stop</span>
            <span className="text-sm font-semibold tracking-normal text-white">{startStop}</span>
          </div>
          <Slider
            className="w-full"
            value={[startStop]}
            min={0}
            max={10000}
            step={25}
            onValueChange={(value) => setStartStop(value[0] ?? startStop)}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-white/60">
            <span>Max end</span>
            <span className="text-sm font-semibold tracking-normal text-white">{endStop}</span>
          </div>
          <Slider
            className="w-full"
            value={[endStop]}
            min={0}
            max={30000}
            step={25}
            onValueChange={(value) => setEndStop(value[0] ?? endStop)}
          />
        </div>
      </div>
    </section>
  );
}
